import type { Task, TaskReference } from './types';

/**
 * Where a task reference opens -- the mechanism, not the destinations.
 *
 * A reference names the section that wrote it and carries that section's own
 * data. Only that section knows what is inside it or where it leads, so each
 * one registers its own resolver: nothing about absences, qualifications or
 * schedules is written here, and adding a section costs no change to this
 * file. The same arrangement as the backend, where a Django app claims its
 * reference type in `ready()`.
 */

export type TaskReferenceRoute = {
  to: string;
  params?: Record<string, string>;
  search?: Record<string, unknown>;
};

export type TaskReferenceResolver = (
  data: Record<string, unknown>,
) => TaskReferenceRoute | null;

const resolvers: Record<string, TaskReferenceResolver> = {};

/** Declare where one section's references open. Called once, at startup. */
export function registerTaskReferenceResolvers(
  entries: Record<string, TaskReferenceResolver>,
): void {
  Object.assign(resolvers, entries);
}

export function canResolveTaskReferenceRoute(
  reference: TaskReference,
): boolean {
  return Boolean(reference.type && reference.type in resolvers);
}

export function resolveTaskReferenceRoute(
  reference: TaskReference,
): TaskReferenceRoute | null {
  if (!reference.type) {
    return null;
  }

  const resolve = resolvers[reference.type];
  if (!resolve) {
    return null;
  }

  return resolve(reference.data ?? {});
}

/** Search param naming the task the list should open on arrival. */
export const TASK_FOCUS_PARAM = 'taskId';

/**
 * Where clicking a task should take somebody: to wherever they can act on it.
 *
 * Two kinds of task, told apart by who is allowed to close them. A
 * system-completable one is waiting on a decision taken somewhere else -- a
 * request is answered by approving or denying the thing it names -- so it opens
 * that thing, and closes itself once the decision lands.
 *
 * Everything else is closed by hand, and the list is where that is done. This
 * is also the honest destination for a reference that no longer leads anywhere:
 * approving a withdrawal deletes the days it gave back, so the notification
 * raised about it outlives its own subject, and the only thing still to do with
 * it is tick it off.
 */
export function resolveTaskActionRoute(task: Task): TaskReferenceRoute {
  if (task.taskType.systemCompletable) {
    const route = resolveTaskReferenceRoute(task.reference);
    if (route) {
      return route;
    }
  }

  return { to: '/tasks', search: { [TASK_FOCUS_PARAM]: String(task.id) } };
}
